import { ResponseGridView } from '../view/ResponseGridView';
import { templateResponseGrid } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { ResponseGridWebComponent } from '..';
import { GridViews, ResponseGridItem, ResponseGridWebComponentSchema, Status } from '../types';
import { SmartViewEvents } from '../../../grid/gridSmartViews/types';
import { ResponseViewSettingsDataProvider } from '../dataProvider/ResponseViewSettingsDataProvider';
import { ResponseConstants } from '../ResponseConstants';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { GridEvents } from '../../../grid/types/common';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import _ from 'lodash';
import { convertSmartConfigToViewConfig } from '../../../grid/utils/smartViews';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { Rights } from '@nirvana/shared/constants';
import { translateFn } from '../../../utils/translator.helper';
import { getChangeStatusFailureMessage } from '../utils';

export class ResponseGridController extends BaseWebComponentController {
  private selectedItems: Array<ResponseGridItem> = [];

  public component: ResponseGridWebComponent;
  public view: ResponseGridView;
  public viewConstructor: typeof ResponseGridView;
  public viewSettingsProviderConstructor: typeof ResponseViewSettingsDataProvider;
  public viewSettingsProvider: ResponseViewSettingsDataProvider;

  private static canBeExecuted(item: ResponseGridItem): boolean {
    return item.status === Status.TO_BE_EXECUTED && ResponseConstants.EXECUTABLE_ACTION_TYPES.includes(item.type);
  }

  constructor(component: ResponseGridWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: ResponseGridView,
      viewTemplate: templateResponseGrid,
    });

    this.viewSettingsProviderConstructor = ResponseViewSettingsDataProvider;
  }

  build() {
    super.build();

    this.viewSettingsProvider = new this.viewSettingsProviderConstructor();
    this.viewSettingsProvider.injectDependencies(this.dependencies);
  }

  async getDataForView(): Promise<ResponseGridWebComponentSchema> {
    return this.viewSettingsProvider.getDataForView();
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.onChangeView.bind(this),
      },
      {
        element: this.view.dom.$manageButtonSelections,
        // event that gets threw when a <gz-select> changes it's value
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.onActionClick.bind(this),
      },
      {
        element: this.view.dom.$executeButton,
        event: 'click',
        callback: this.onActionClick.bind(this),
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.onSelectionChange.bind(this),
      },
    ];
  }

  private async onChangeView(e: WebComponentEvent) {
    await this.setView(e.detail.smartViewConfiguration);
  }

  public async setGridView(gridView: GridViews, filters: any) {
    const viewConfig = this.view.viewSettings.views.find(view => view.id === gridView);

    if (!viewConfig) {
      return;
    }

    // merge filters
    const newConfig = _.cloneDeep(viewConfig);

    filters.forEach(filter => {
      const filterKey = Object.keys(filter)[0];

      const identifiedFilter = newConfig.filters.find(
        viewConfigFilter => filterKey === Object.keys(viewConfigFilter)[0]
      );

      if (identifiedFilter) {
        identifiedFilter[filterKey] = filter[filterKey];
      } else {
        newConfig.filters.push(filter);
      }
    });

    await this.setView(newConfig);
  }

  async setView(smartConfig) {
    // Make the smart views unclickable while the grid is reset as to avoid
    // bugs with async (data loading wrong because the user rapidly clicks different views
    this.view.dom.$smartViews.style.pointerEvents = 'none';

    this.view.dom.$grid.propertiesSet = false;
    const viewConfig = convertSmartConfigToViewConfig(smartConfig);

    await this.view.gridDataProvider.setFilters({
      viewId: viewConfig.id,
      filters: viewConfig.filters,
    });
    this.view.gridDataProvider.setColumns(viewConfig.displayedColumns);

    this.view.dom.$smartViews.propertiesSet = false;
    this.view.dom.$smartViews.activeViewId = viewConfig.id;
    this.view.dom.$smartViews.propertiesSet = true;

    this.view.setViewProps(viewConfig);
    this.view.dom.$grid.propertiesSet = true;

    // Enable smart view clicks when done
    this.view.dom.$smartViews.style.pointerEvents = 'auto';
  }

  private getSelectionIds(): Array<string> {
    return this.selectedItems.map(record => record['_id']);
  }

  private async makeRpcRequest(idsArray: Array<string>, result: Array<any>, status: Status) {
    // the backend does not support rpc calls with empty id array
    if (!isEmpty(idsArray)) {
      try {
        const rpcResponse = await this.dependencies.api.clients.GridItemsProvider.setRecordsStatus({
          actionIds: idsArray,
          newStatus: status,
        });

        // the 'nrSentForExecution' field is added in case that the second call fails, so I need to count
        // the overall nrSentForExecution
        result.push({ data: { nrSentForExecution: idsArray.length }, ...rpcResponse });
      } catch (e) {
        result.push(e);
      }
    }
  }

  /*
   * The backend does not support rpc calls with multiple statuses, therefore, I need to split the call into 2 calls
   * */
  private async filterExecutableTypesFromExecuteStatus(): Promise<Array<any>> {
    const executableTypeIds = this.selectedItems
      .filter(selection => ResponseConstants.EXECUTABLE_ACTION_TYPES.includes(selection.type))
      .map(selection => selection._id);
    const externalActionNeededTypeIds = this.selectedItems
      .filter(selection => !ResponseConstants.EXECUTABLE_ACTION_TYPES.includes(selection.type))
      .map(selection => selection._id);
    const result: any[] = [];

    // I need to try to execute both queries first,
    // I can't throw an error as soon as the first rpc call fails, because in case that the second one
    // goes through, I need to calculate and display how many actions were executed.

    await this.makeRpcRequest(executableTypeIds, result, Status.TO_BE_EXECUTED);
    // at this point, the 'result' variable will contain one of the following : nothing | a backed response | a backend error
    await this.makeRpcRequest(externalActionNeededTypeIds, result, Status.EXTERNAL_ACTION_NEEDED);

    return result;
  }

  private async treatMultipleRestoreStatuses() {
    const response = await this.filterExecutableTypesFromExecuteStatus();
    let nrSentForExecution = 0;
    let code: any = null;

    // if there is an error, it means partial execution, so I need to throw an error after iterating the response array
    let thereIsAnError = false;

    response.forEach(element => {
      if (element['data']?.['nrSentForExecution']) {
        nrSentForExecution += element['data']['nrSentForExecution'];
      }

      if (element['code'] === ResponseConstants.ERROR_CODE_PARTIAL_EXECUTION) {
        code = ResponseConstants.ERROR_CODE_PARTIAL_EXECUTION;
        thereIsAnError = true;
      }
    });

    if (thereIsAnError) {
      // this is done in order to reuse the catch logic
      // throw an object that contains code ERROR_CODE_PARTIAL_EXECUTION if there was one
      // otherwise, thrown a null code in order to signal a totally failed action
      const exceptionObject = {
        code: code,
      };

      if (code !== null) {
        exceptionObject['data'] = {
          nrSentForExecution: nrSentForExecution,
        };
      }

      throw exceptionObject;
    }

    return { success: true };
  }

  private displayPartialExecutionToast(e: WebComponentEvent, error: any, numberOfSelectedRows: number) {
    let clientMessage;
    if (!e.detail['selected']) {
      // execute
      const failedActionsCount = numberOfSelectedRows - error['data']['nrSentForExecution'];
      clientMessage = this.dependencies.translateFn(
        '{NR_FAILED} out of {NR_TOTAL} response actions failed to initiate',
        {
          NR_FAILED: failedActionsCount.toString(),
          NR_TOTAL: numberOfSelectedRows.toString(),
        }
      );
    } else {
      // manage
      clientMessage = this.dependencies.translateFn('Status change failed for {NR_FAILED} out of {NR_TOTAL} actions', {
        NR_FAILED: (numberOfSelectedRows - error['data']['nrSentForExecution']).toString(),
        NR_TOTAL: numberOfSelectedRows.toString(),
      });
    }
    this.showToast(ToastSeverity.WARNING, clientMessage);
  }

  private async onActionClick(e: WebComponentEvent) {
    // execute and manage buttons only
    const selectionIds = this.getSelectionIds();
    const responseConstants = this.dependencies.responseConstants;
    const newStatus = e.detail['selected'] !== undefined ? e.detail['selected'] : Status.PENDING;

    try {
      let response;

      if (newStatus === Status.TO_BE_EXECUTED) {
        // it's the restore button
        response = await this.treatMultipleRestoreStatuses();
      } else {
        response = await this.dependencies.api.clients.GridItemsProvider.setRecordsStatus({
          actionIds: selectionIds,
          newStatus: newStatus,
        });
      }

      if (response['success']) {
        const pluralMessageBuilder = selectionIds.length + responseConstants.TOAST_MESSAGES_SUCCESSFUL[newStatus][1];
        const clientMessage =
          selectionIds.length > 1 ? pluralMessageBuilder : responseConstants.TOAST_MESSAGES_SUCCESSFUL[newStatus][0];
        this.showToast(ToastSeverity.SUCCESS, this.dependencies.translateFn(clientMessage));
      }
    } catch (error) {
      if (error['code'] === ResponseConstants.ERROR_CODE_PARTIAL_EXECUTION) {
        this.displayPartialExecutionToast(e, error, selectionIds.length);
      } else {
        this.showToast(
          ToastSeverity.ERROR,
          getChangeStatusFailureMessage(
            Status.PENDING === newStatus,
            error['code'],
            selectionIds.length,
            responseConstants
          )
        );
      }
    } finally {
      await this.view.dom.$grid.controller.handleGridItemsLoad();
    }
  }

  private showToast(severity: any, message: string) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.dependencies.translateFn(message),
      severity: severity,
    });
  }

  private onSelectionChange(event: WebComponentEvent) {
    this.selectedItems = event.detail.selectedItemsData;
    this.view.dom.$executeTooltip.tooltipText = '';

    if (this.selectedItems.length <= 0 || !Application.Main.hasAnyRights([Rights.RIGHT_MANAGE_INVENTORY])) {
      this.view.dom.$executeButton.disabled = true;
      this.view.dom.$manageButtonSelections.disabled = true;
      return;
    }

    const responseConstants = this.dependencies.responseConstants;

    let executeButtonStatus = false;

    // We enable the manage button even if the user can not manage any response action so
    // that he/she may see what options are theoretically available
    this.view.dom.$manageButtonSelections.disabled = false;

    const manageActionStatuses = {};
    const manageActionsNames = Object.keys(responseConstants.MANAGE_ACTIONS);

    // Be default we can't execute any manage selection action
    manageActionsNames.forEach(selection => (manageActionStatuses[selection] = false));

    // Iterate through the selection to see what actions we can do
    this.selectedItems.forEach(responseAction => {
      if (ResponseGridController.canBeExecuted(responseAction)) {
        executeButtonStatus = true;
      }

      // Check for each option from the dropdown
      manageActionsNames.forEach(manageActionName => {
        if (responseConstants.MANAGE_ACTIONS[manageActionName].canBeDone(responseAction)) {
          manageActionStatuses[manageActionName] = true;
        }
      });
    });

    if (this.dependencies.isGzDemo) {
      executeButtonStatus = false;
      manageActionsNames.forEach(selection => (manageActionStatuses[selection] = false));
      this.view.dom.$executeTooltip.tooltipText = translateFn('Not available in demo mode.');
    }

    // Rebuild manage actions with appropriate disabled/enabled status
    this.view.dom.$manageButtonSelections.options = manageActionsNames.map(manageActionName => {
      const manageAction = responseConstants.MANAGE_ACTIONS[manageActionName];

      return {
        title: manageAction.title,
        value: manageAction.value,
        disabled: !manageActionStatuses[manageActionName],
      };
    });

    this.view.dom.$executeButton.disabled = !executeButtonStatus;
  }
}
